import React, { useEffect, useState } from "react";
import { RefreshCcw, Search } from "react-feather";
import Modal from "react-bootstrap/Modal";
import ReactPaginate from "react-paginate";
import { GET, PGET, POST } from "../../../api/adminApi";
import { formatPhone } from "../../../helper";
import { toast } from "react-toastify";

function normalizePhone(phone) {
  if (!phone) return "";
  return phone.replace(/\D/g, "");
}

function asNumber(value) {
  const n = Number(value);
  return isNaN(n) ? 0 : n;
}

function UserCard({ user, title }) {
  return (
    <div className="mt-3 border-top pt-3">
      <h6 className="text-muted mb-3">{title}</h6>
      <div className="row">
        <div className="col-6 mb-2"><strong>Ф.И.О.:</strong> {user.fullName || "-"}</div>
        <div className="col-6 mb-2"><strong>Телефон:</strong> {formatPhone(normalizePhone(user.phone || user.login)) || "-"}</div>
        <div className="col-6 mb-2"><strong>Активен:</strong> {user.activated ? "Да" : "Нет"}</div>
        <div className="col-6 mb-2"><strong>Область:</strong> {user.regionName || "-"}</div>
        <div className="col-6 mb-2"><strong>Город:</strong> {user.cityName || "-"}</div>
        <div className="col-6 mb-2"><strong>Текущий балл:</strong> {asNumber(user.earnedPoint)}</div>
        <div className="col-6 mb-2"><strong>Панель:</strong> {asNumber(user.earnedPointPanel)}</div>
        <div className="col-6 mb-2"><strong>Секция:</strong> {asNumber(user.earnedPointSection)}</div>
        <div className="col-6 mb-2"><strong>Реферал:</strong> {asNumber(user.earnedPointReferal)}</div>
        <div className="col-6 mb-2"><strong>Royal:</strong> {asNumber(user.earnedPointRoyal)}</div>
        <div className="col-6 mb-2"><strong>Общий:</strong> {asNumber(user.earnedPointOverall)}</div>
      </div>
    </div>
  );
}

function ComparisonTable({ preview }) {
  const { fromUser, toUser } = preview;
  const rows = [
    { label: "Текущий балл", earnedKey: "earnedPoint", pointKey: "point" },
    { label: "Панель", earnedKey: "earnedPointPanel", pointKey: "pointPanel" },
    { label: "Секция", earnedKey: "earnedPointSection", pointKey: "pointSection" },
    { label: "Реферал", earnedKey: "earnedPointReferal", pointKey: "pointReferal" },
    { label: "Royal", earnedKey: "earnedPointRoyal", pointKey: "pointRoyal" },
    { label: "Общий", earnedKey: "earnedPointOverall", pointKey: "pointOverall" },
  ];

  return (
    <div className="table-responsive mt-4 p-3 bg-white border rounded">
      <table className="table table-bordered mb-0">
        <thead>
          <tr>
            <th>Поле</th>
            <th className="text-end">Старый (FROM)</th>
            <th className="text-end">Новый (TO)</th>
            <th className="text-end">После перевода</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const from = asNumber(fromUser[r.earnedKey]);
            const to = asNumber(toUser[r.earnedKey]);
            const add = asNumber(preview[r.pointKey]);
            return (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className="text-end">{from}</td>
                <td className="text-end">{to}</td>
                <td className="text-end fw-bold">{to + add}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PointsTransfer() {
  const [fromPhone, setFromPhone] = useState("");
  const [toPhone, setToPhone] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const [result, setResult] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageCount, setHistoryPageCount] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);

  function clearPreview() {
    setPreview(null);
    setResult(null);
    setError("");
  }

  async function fetchHistory(nextPage = historyPage, nextSearch = historySearch) {
    setHistoryLoading(true);
    try {
      const params = {
        page: nextPage,
        size: 20,
        sort: "id,desc",
      };

      const trimmedSearch = nextSearch?.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await PGET("/services/admin/api/points-transfer/history", true, params);

      const items = Array.isArray(response?.data) ? response.data : [];
      const total = Number(response?.headers?.["x-total-count"] || response?.headers?.["X-Total-Count"] || 0);
      setHistoryItems(items);
      setHistoryTotal(total);
      setHistoryPageCount(Math.max(1, Math.ceil(total / 20)));
    } catch (e) {
      setHistoryItems([]);
      setHistoryTotal(0);
      setHistoryPageCount(0);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    fetchHistory(historyPage, historySearch);
  }, [historyPage, historySearch]);

  async function loadPreview() {
    const from = normalizePhone(fromPhone);
    const to = normalizePhone(toPhone);
    if (from.length < 5) {
      setError("Введите полный старый номер");
      setPreview(null);
      setResult(null);
      return;
    }
    if (to.length < 5) {
      setError("Введите полный новый номер");
      setPreview(null);
      setResult(null);
      return;
    }
    if (from === to) {
      setError("Старый и новый номер не могут быть одинаковыми");
      setPreview(null);
      setResult(null);
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const res = await GET(`/services/admin/api/points-transfer/preview?fromPhone=${from}&toPhone=${to}`, true, true);
      if (!res) {
        setError("Произошла ошибка");
        setPreview(null);
      } else if (res.success === false) {
        setError(res.message || "Произошла ошибка");
        setPreview(null);
      } else {
        setPreview(res);
      }
    } catch (e) {
      setError("Произошла ошибка");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  function handleFromChange(e) {
    setFromPhone(e.target.value);
    clearPreview();
  }

  function handleToChange(e) {
    setToPhone(e.target.value);
    clearPreview();
  }

  function openConfirm() {
    setReason("");
    setShowConfirm(true);
  }

  async function submitTransfer() {
    setSubmitting(true);
    try {
      const res = await POST("/services/admin/api/points-transfer", {
        fromPhone: normalizePhone(fromPhone),
        toPhone: normalizePhone(toPhone),
        reason: reason || "",
      }, true);
      if (res && res.success) {
        toast.success(res.message || "Баллы успешно переведены");
        setFromPhone("");
        setToPhone("");
        setResult(null);
        setPreview(null);
        setError("");
        setShowConfirm(false);
        setReason("");
        setHistorySearch("");
        setHistoryPage(0);
        await fetchHistory(0, "");
      } else {
        const err = res?.response?.data || res || {};
        const msg = err.message || "Произошла ошибка";
        setError(msg);
        toast.error(msg);
      }
    } catch (e) {
      const msg = "Произошла ошибка";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="br-5 card" style={{ background: "#eef1ff", marginTop: "-0.9rem", minHeight: "100vh" }}>
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">Перевод баллов</span>
        </h3>
      </div>

      <div className="px-3 pb-4 pt-2">
        <div className="card p-3 mb-3 bg-white">
          <div className="row g-3 align-items-end">
            <div className="col-md-5">
              <label className="form-label">Старый номер</label>
              <input
                type="text"
                className="form-control"
                value={fromPhone}
                onChange={handleFromChange}
                placeholder="998901111111"
              />
            </div>
            <div className="col-md-5">
              <label className="form-label">Новый номер</label>
              <input
                type="text"
                className="form-control"
                value={toPhone}
                onChange={handleToChange}
                placeholder="998902222222"
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-danger w-100 d-flex align-items-center justify-content-center"
                onClick={loadPreview}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                  <>
                    <Search size={16} className="mr-1" /> Загрузить
                  </>
                )}
              </button>
            </div>
          </div>
          {error && <div className="text-danger mt-3 small">{error}</div>}
        </div>

        {preview && preview.fromUser && preview.toUser && (
          <>
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="card p-3 h-100 bg-white">
                  <h5>ОТКУДА</h5>
                  <UserCard user={preview.fromUser} title="Старый номер" />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="card p-3 h-100 bg-white">
                  <h5>КУДА</h5>
                  <UserCard user={preview.toUser} title="Новый номер" />
                </div>
              </div>
            </div>

            {preview.fromUser?.hasActivePrizeRequest && (
              <div className="alert alert-danger">
                У старого номера есть активный запрос на приз. Сначала завершите запрос на приз.
              </div>
            )}

            {!preview.canTransfer && (
              <div className="alert alert-warning">
                {preview.message || "Перевод невозможен"}
              </div>
            )}

            <ComparisonTable preview={preview} />

            <div className="d-flex justify-content-end mt-3">
              <button
                className="btn btn-success"
                onClick={openConfirm}
                disabled={!preview.canTransfer || loading || submitting}
              >
                <RefreshCcw size={16} className="mr-1" /> Перевести
              </button>
            </div>
          </>
        )}

        {result && result.success && (
          <div className="alert alert-success mt-3">
            <div className="fw-bold mb-2">{result.message}</div>
            <div className="row small">
              <div className="col-6"><strong>FROM:</strong> {result.fromPhone}</div>
              <div className="col-6"><strong>TO:</strong> {result.toPhone}</div>
              {[
                { label: "Текущий балл", key: "point" },
                { label: "Панель", key: "pointPanel" },
                { label: "Секция", key: "pointSection" },
                { label: "Реферал", key: "pointReferal" },
                { label: "Royal", key: "pointRoyal" },
                { label: "Общий", key: "pointOverall" },
              ].map((f) => (
                <div key={f.key} className="col-6">
                  <strong>{f.label}:</strong> {asNumber(result[f.key])}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card p-3 mt-4 bg-white">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0">История переводов</h5>
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 320 }}
              value={historySearch}
              onChange={(e) => {
                setHistorySearch(e.target.value);
                setHistoryPage(0);
              }}
              placeholder="Поиск по номеру, имени, причине, администратору"
            />
          </div>

          {historyLoading ? (
            <div className="text-center py-4">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            </div>
          ) : historyItems.length ? (
            <>
              <div className="table-responsive">
                <table className="table table-bordered mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Откуда</th>
                      <th>Куда</th>
                      <th className="text-end">Текущий</th>
                      <th className="text-end">Панель</th>
                      <th className="text-end">Секция</th>
                      <th className="text-end">Реферал</th>
                      <th className="text-end">Royal</th>
                      <th>Причина</th>
                      <th>Админ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyItems.map((item) => (
                      <tr key={item.id}>
                        <td>{item.createdAt || "-"}</td>
                        <td>
                          <div>{item.fromPhone || "-"}</div>
                          <small className="text-muted">{item.fromFullName || "-"}</small>
                        </td>
                        <td>
                          <div>{item.toPhone || "-"}</div>
                          <small className="text-muted">{item.toFullName || "-"}</small>
                        </td>
                        <td className="text-end">{asNumber(item.point)}</td>
                        <td className="text-end">{asNumber(item.pointPanel)}</td>
                        <td className="text-end">{asNumber(item.pointSection)}</td>
                        <td className="text-end">{asNumber(item.pointReferal)}</td>
                        <td className="text-end">{asNumber(item.pointRoyal)}</td>
                        <td>{item.reason || "-"}</td>
                        <td>{item.performedBy || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {historyPageCount > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <ReactPaginate
                    previousLabel={"<"}
                    nextLabel={">"}
                    breakLabel={"..."}
                    pageCount={historyPageCount}
                    forcePage={historyPage}
                    onPageChange={(page) => setHistoryPage(page.selected)}
                    containerClassName={"pagination mb-0"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    previousClassName={"page-item"}
                    nextClassName={"page-item"}
                    previousLinkClassName={"page-link"}
                    nextLinkClassName={"page-link"}
                    activeClassName={"active"}
                    breakClassName={"page-item"}
                    breakLinkClassName={"page-link"}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-muted py-3">Данных нет</div>
          )}
        </div>

        <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Подтверждаете перевод баллов?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Все баллы с <strong>{formatPhone(normalizePhone(fromPhone))}</strong> ({preview?.fromUser?.fullName || "-"}) будут переведены на{" "}
              <strong>{formatPhone(normalizePhone(toPhone))}</strong> ({preview?.toUser?.fullName || "-"}).
              Баллы на старом номере станут 0. Это действие нельзя отменить.
            </p>
            <div className="form-group">
              <label>Причина</label>
              <input
                type="text"
                className="form-control"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Смена номера телефона"
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowConfirm(false)} disabled={submitting}>
              Отмена
            </button>
            <button className="btn btn-danger" onClick={submitTransfer} disabled={submitting}>
              {submitting ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                "Да, перевести"
              )}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default PointsTransfer;
